// Approximate geographic centroids (lat, lng) for all 27 Brazilian federative
// units. Used by the FranchiseLocator to compute "nearest franchises" without
// requiring per-franchise lat/lng. Values are good enough (within ~50 km) for
// proximity ordering between states.

export const STATE_CENTROIDS = {
  AC: { name: 'Acre', lat: -9.0238, lng: -70.8120 },
  AL: { name: 'Alagoas', lat: -9.5713, lng: -36.7820 },
  AM: { name: 'Amazonas', lat: -3.4168, lng: -65.8561 },
  AP: { name: 'Amapá', lat: 0.9020, lng: -52.0030 },
  BA: { name: 'Bahia', lat: -12.5797, lng: -41.7007 },
  CE: { name: 'Ceará', lat: -5.4984, lng: -39.3206 },
  DF: { name: 'Distrito Federal', lat: -15.8267, lng: -47.9218 },
  ES: { name: 'Espírito Santo', lat: -19.1834, lng: -40.3089 },
  GO: { name: 'Goiás', lat: -15.8270, lng: -49.8362 },
  MA: { name: 'Maranhão', lat: -4.9609, lng: -45.2744 },
  MG: { name: 'Minas Gerais', lat: -18.5122, lng: -44.5550 },
  MS: { name: 'Mato Grosso do Sul', lat: -20.7722, lng: -54.7852 },
  MT: { name: 'Mato Grosso', lat: -12.6819, lng: -56.9211 },
  PA: { name: 'Pará', lat: -3.4168, lng: -52.0000 },
  PB: { name: 'Paraíba', lat: -7.2399, lng: -36.7819 },
  PE: { name: 'Pernambuco', lat: -8.8137, lng: -36.9541 },
  PI: { name: 'Piauí', lat: -7.7183, lng: -42.7289 },
  PR: { name: 'Paraná', lat: -24.8932, lng: -51.4915 },
  RJ: { name: 'Rio de Janeiro', lat: -22.9099, lng: -43.2095 },
  RN: { name: 'Rio Grande do Norte', lat: -5.4026, lng: -36.9541 },
  RO: { name: 'Rondônia', lat: -11.5057, lng: -63.5806 },
  RR: { name: 'Roraima', lat: 2.7376, lng: -62.0751 },
  RS: { name: 'Rio Grande do Sul', lat: -30.0346, lng: -53.0000 },
  SC: { name: 'Santa Catarina', lat: -27.2423, lng: -50.2189 },
  SE: { name: 'Sergipe', lat: -10.5741, lng: -37.3857 },
  SP: { name: 'São Paulo', lat: -23.5505, lng: -46.6333 },
  TO: { name: 'Tocantins', lat: -10.1753, lng: -48.2982 },
};

// Reverse lookup by full state name (canonical, accent-sensitive)
export const STATE_NAME_TO_CODE = Object.entries(STATE_CENTROIDS).reduce(
  (acc, [code, { name }]) => {
    acc[name] = code;
    return acc;
  },
  {},
);

// Sorted list for dropdowns: { code, name, lat, lng }
export const STATES_LIST = Object.entries(STATE_CENTROIDS)
  .map(([code, v]) => ({ code, ...v }))
  .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

// Haversine distance in km between two {lat,lng} points
export const haversineKm = (a, b) => {
  if (!a || !b) return Infinity;
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
};

// Resolve a free-form state string (sigla or full name) to canonical {code, name, lat, lng}
export const resolveState = (raw) => {
  if (!raw) return null;
  const upper = raw.trim().toUpperCase();
  if (STATE_CENTROIDS[upper]) return { code: upper, ...STATE_CENTROIDS[upper] };
  const trimmed = raw.trim();
  const code = STATE_NAME_TO_CODE[trimmed];
  if (code) return { code, ...STATE_CENTROIDS[code] };
  // case-insensitive fallback
  const lowered = trimmed.toLowerCase();
  for (const [code, v] of Object.entries(STATE_CENTROIDS)) {
    if (v.name.toLowerCase() === lowered) return { code, ...v };
  }
  return null;
};
